import { useState } from 'react'
import { Check, Lock, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appLabel } from '@/lib/app-label'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { SeedAppConfig } from '@/types'
import { chipKeys, dormantKeys, isOn, resetToAll, toggle } from './workspace-apps-state'

/** Contract C1: the words on a restricted app's chip. */
const RESTRICTED_TOOLTIP = 'GFNet admin: Farmer1st staff only'

/**
 * Per-workspace app multiselect — one button per app available on the seed's served
 * version, plus a disabled chip for any DORMANT key: an app the workspace still has
 * on record that the served version no longer offers. Dormant keys are preserved on
 * save (a version change never silently drops them), so they must be visible and must
 * not be counted when deciding whether the selection covers everything.
 *
 * A RESTRICTED app (BARN-345) is drawn in red: "all apps" never includes it, only a
 * workspace naming it reaches it, and turning it on asks for confirmation in the page.
 * The toggle logic lives in `workspace-apps-state.ts`.
 */
export function WorkspaceApps({
  apps,
  allApps,
  appsConfig,
  restrictedApps,
  seedDisplayName,
  onChange,
}: {
  apps: string[] | null
  allApps: string[]
  /** Manifest app config from the served version — supplies the labels. */
  appsConfig?: Record<string, SeedAppConfig>
  /** The served version's restricted apps (their appsConfig entries); absent = none. */
  restrictedApps?: Record<string, SeedAppConfig>
  /** Stripped from label prefixes, exactly as the container's app bar does. */
  seedDisplayName?: string
  onChange: (next: string[] | null) => void
}) {
  const [confirming, setConfirming] = useState<string | null>(null)
  const restrictedKeys = Object.keys(restrictedApps ?? {})
  const label = (a: string) =>
    appLabel(a, restrictedApps?.[a] ? restrictedApps : appsConfig, seedDisplayName)
  const all = chipKeys(allApps, restrictedKeys)
  const dormant = dormantKeys(apps, all)

  function press(a: string) {
    const turningOnRestricted = restrictedKeys.includes(a) && !isOn(apps, a, restrictedKeys)
    if (turningOnRestricted) {
      setConfirming(a)
      return
    }
    onChange(toggle(apps, a, all, restrictedKeys))
  }

  function acceptTurnOn() {
    if (confirming) onChange(toggle(apps, confirming, all, restrictedKeys))
    setConfirming(null)
  }

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-1">
        {all.length === 0 && <span className="text-xs text-muted-foreground">No apps available</span>}
        {all.map(a => {
          const enabled = isOn(apps, a, restrictedKeys)
          const restricted = restrictedKeys.includes(a)
          const chip = (
            <button
              key={a}
              type="button"
              onClick={() => press(a)}
              className={cn(
                'inline-flex items-center gap-0.5 rounded border px-1.5 py-0 text-xs font-medium transition-colors cursor-pointer',
                restricted
                  ? enabled
                    ? 'bg-destructive/10 border-destructive/40 text-destructive hover:border-destructive/60'
                    : 'bg-transparent border-destructive/40 text-destructive/80 line-through hover:border-destructive/60'
                  : enabled
                    ? 'bg-primary/10 border-primary/30 text-primary hover:border-primary/50'
                    : 'bg-muted/50 border-transparent text-muted-foreground line-through hover:border-muted-foreground/30',
              )}
            >
              {enabled ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
              {label(a)}
              {restricted && <Lock className="h-3 w-3" />}
            </button>
          )
          if (!restricted) return chip
          return (
            <Tooltip key={a}>
              <TooltipTrigger asChild>{chip}</TooltipTrigger>
              <TooltipContent>{RESTRICTED_TOOLTIP}</TooltipContent>
            </Tooltip>
          )
        })}
        {dormant.map(a => (
          <span
            key={a}
            title={`"${a}" is kept on record but is not available on the version this seed currently serves. It stays saved and becomes active again if a version offering it is served.`}
            className="inline-flex items-center gap-0.5 rounded border border-dashed border-muted-foreground/40 px-1.5 py-0 text-xs font-medium text-muted-foreground"
          >
            {label(a)} · not in this version
          </span>
        ))}
        {apps !== null && (
          <button
            type="button"
            onClick={() => onChange(resetToAll())}
            className="text-xs text-muted-foreground hover:text-foreground underline ml-2"
            title="Reset to all apps (restricted apps stay off)"
          >
            all
          </button>
        )}
      </div>

      <Dialog open={confirming !== null} onOpenChange={open => { if (!open) setConfirming(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn on {confirming ? label(confirming) : ''}?</DialogTitle>
            <DialogDescription>
              {RESTRICTED_TOOLTIP}. Every member of this workspace will reach it. "All apps" never
              includes it: it is on only while this workspace names it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={acceptTurnOn}>Turn on</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
